import { useState } from "react";
import { MapPin, Users, ArrowRight, Question } from "@phosphor-icons/react";
import { saveSession } from "@/lib/session";
import { toast } from "sonner";
import HowItWorks from "@/components/HowItWorks";

export default function JoinScreen({ onJoin }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("code");
      return p ? p.toUpperCase() : "";
    } catch {
      return "";
    }
  });
  const [showHelp, setShowHelp] = useState(false);
  const invitedCode = (() => {
    try {
      return new URLSearchParams(window.location.search).get("code")?.toUpperCase() || "";
    } catch {
      return "";
    }
  })();

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Enter your name");
    if (!code.trim()) return toast.error("Enter a group code");
    const session = saveSession({ name, groupCode: code });
    toast.success(`You're in — ${session.groupCode}`);
    onJoin(session);
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-secondary">
      <img
        src="https://images.pexels.com/photos/12657546/pexels-photo-12657546.jpeg"
        alt="festival"
        className="absolute inset-0 h-full w-full object-cover opacity-40"
      />
      <div className="absolute inset-0 noise" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-secondary/30 to-secondary" />

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-5 py-10">
        <div className="mb-6 text-center">
          <div className="mb-3 inline-flex items-center gap-2 border-2 border-black bg-primary px-3 py-1 shadow-[4px_4px_0px_rgba(0,0,0,1)]">
            <MapPin weight="fill" size={18} />
            <span className="font-mono2 text-xs font-bold uppercase tracking-[0.2em]">unlostcrew</span>
          </div>
          <h1 className="font-heading text-4xl font-black uppercase leading-none tracking-tighter text-white sm:text-5xl">
            Never Lose<br />
            Your <span className="text-primary">Crew</span>
          </h1>
          <p className="mx-auto mt-3 max-w-xs font-mono2 text-sm font-medium text-white/70">
            Type your name, share one code, and everyone shows up on the map — even when the signal drops.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="join-form"
          className="w-full max-w-sm border-2 border-black bg-white p-5 shadow-[6px_6px_0px_rgba(0,0,0,1)]"
        >
          {invitedCode && (
            <div data-testid="invited-banner" className="mb-4 border-2 border-black bg-accent px-3 py-2 text-center font-mono2 text-xs font-bold uppercase text-white shadow-[3px_3px_0px_rgba(0,0,0,1)]">
              You're invited to <span className="font-heading tracking-widest">{invitedCode}</span> — just add your name
            </div>
          )}
          <label className="font-mono2 text-xs font-bold uppercase tracking-[0.2em] text-black/60">Your Name</label>
          <div className="mb-4 mt-1 flex items-center border-2 border-black bg-white">
            <span className="grid h-14 w-12 place-items-center border-r-2 border-black bg-secondary text-white">
              <Users weight="bold" size={20} />
            </span>
            <input
              data-testid="join-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sam"
              maxLength={24}
              className="h-14 w-full bg-white px-3 font-mono2 text-lg font-semibold outline-none placeholder:text-black/30"
            />
          </div>

          <label className="font-mono2 text-xs font-bold uppercase tracking-[0.2em] text-black/60">Group Code</label>
          <div className="mb-5 mt-1 flex items-center border-2 border-black bg-white">
            <span className="grid h-14 w-12 place-items-center border-r-2 border-black bg-accent text-white">
              <MapPin weight="fill" size={20} />
            </span>
            <input
              data-testid="join-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="CAMP-42"
              maxLength={16}
              className="h-14 w-full bg-white px-3 font-mono2 text-lg font-bold uppercase tracking-widest outline-none placeholder:text-black/30"
            />
          </div>

          <button
            data-testid="join-button"
            type="submit"
            className="flex h-16 w-full items-center justify-center gap-2 border-2 border-black bg-primary font-heading text-xl font-bold uppercase text-black shadow-[4px_4px_0px_#18181B] transition-transform duration-150 ease-out hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#18181B] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none"
          >
            Join The Map <ArrowRight weight="bold" size={22} />
          </button>

          <p className="mt-3 text-center font-mono2 text-[11px] font-medium text-black/50">
            Everyone who types the same code sees each other. No sign-up.
          </p>
        </form>

        <button
          data-testid="join-how-it-works"
          onClick={() => setShowHelp(true)}
          className="mt-4 inline-flex items-center gap-2 border-2 border-black bg-white px-4 py-2 font-mono2 text-xs font-bold uppercase tracking-wider text-black shadow-[3px_3px_0px_rgba(0,0,0,1)] transition-transform duration-150 ease-out active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
        >
          <Question weight="bold" size={16} /> How does it work?
        </button>
      </div>

      <HowItWorks open={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
}
